import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Card, Col, Collapse, type CollapseProps, Input, Row, Skeleton, Typography } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ViewType } from "../../components/CreateNoteModal/AITemplateModal/types";
import TemplateCard from "../../components/TemplateCard/template-card.component";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { type Template, getTemplateBySlug } from "../../service/templates.service";
import { setNotesViewVisible, setSelectedView, setStoredTemplateData } from "../../slices/appStateSlice";
import styles from "./template.module.scss";

const { Title, Text } = Typography;

const TemplateDetailsPage: React.FC = () => {
  const componentName = "template";
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobileView();
  const dispatch = useAppDispatch();
  const [template, setTemplate] = useState<Template>({} as Template);
  const [loading, setLoading] = useState(true);
  const isNotesViewVisible = useAppSelector((state) => state.appState.isNotesViewVisible);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!slug) return;
      try {
        const data = await getTemplateBySlug(slug);
        setTemplate(data);
      } catch (error) {
        console.error("Error fetching template:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [slug]);

  if (loading) {
    return (
      <Row
        gutter={[16, 16]}
        style={{
          padding: "2rem",
          textAlign: "center",
          backgroundColor: "#FFF",
          height: "calc(100vh - 64px - var(--banner-height, 0px))",
        }}
      >
        <Col span={24} md={8}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Skeleton active />
              </Card>
            </Col>
            <Col span={24}>
              <Card style={{ width: "100%" }}>
                <Skeleton active />
              </Card>
            </Col>
          </Row>
        </Col>
        <Col span={24} md={16}>
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
        </Col>
      </Row>
    );
  }

  const handleSave = () => {
    dispatch(
      setStoredTemplateData({
        id: template.id,
        name: template.name,
        document: template.content,
        isFromTemplate: true,
      }),
    );
    dispatch(setSelectedView(ViewType.DASHBOARD));
    if (isNotesViewVisible) {
      dispatch(setNotesViewVisible(false));
    }
    navigate("/home");
  };

  const HowToAddTemplate = () => {
    return (
      <div style={{ textAlign: "left" }}>
        <div>Follow this short guide to make our template your own</div>
        <br />
        <div>
          <strong>1. Review the example note</strong> <br />
          See a complete sample that shows how an audit-ready note is structured using this template.
          <br />
          <br />
          <strong> 2. Add & customize your template </strong>
          <br />
          Click “Add & Edit Template” to make it your own. Nudge’s AI will replicate the note’s structure, tone, and
          writing style.
          <br />
          <br />
          <strong>3. Customize the template (optional)</strong>
          <br />
          You’ll be taken to the editor, where you can update sections, adjust content, and add or remove headers as
          needed.
        </div>
      </div>
    );
  };

  const items: CollapseProps["items"] = [
    {
      key: "1",
      label: <strong>How to Add This Template</strong>,
      children: <HowToAddTemplate />,
    },
  ];

  return (
    <div style={{ backgroundColor: "#FFF", minHeight: "100%" }}>
      <div style={{ padding: "1rem", maxWidth: "1200px", margin: "0 auto" }}>
        <Row style={{ margin: "1rem 0px" }}>
          <Col>
            <Button
              onClick={() => {
                dispatch(setSelectedView(ViewType.TEMPLATE));
                navigate("/templates");
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "4px 10px",
                backgroundColor: "transparent",
              }}
            >
              <ArrowLeftOutlined />
              Back to all Templates
            </Button>
          </Col>
        </Row>

        <Row justify="center" gutter={[16, 16]} style={{ marginBottom: "2rem" }}>
          <Col xs={24} md={10} lg={8}>
            <TemplateCard
              slug={template.slug}
              index={0}
              title={template.name}
              description={template.description}
              content={template.content}
              fit
              hideUse
              hideSocial={false}
            />
            {isMobile && (
              <Collapse
                style={{
                  textAlign: "left",
                  marginTop: "1rem",
                  fontWeight: 500,
                }}
                items={items}
                defaultActiveKey={isMobile ? undefined : ["1"]}
              />
            )}
            {!isMobile && (
              <Card
                style={{
                  textAlign: "left",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                  marginTop: "1rem",
                }}
              >
                <Title
                  level={4}
                  style={{
                    marginBottom: "1rem",
                    textAlign: "left",
                    marginTop: 0,
                  }}
                >
                  How to Add This Template
                </Title>
                <HowToAddTemplate />
              </Card>
            )}
          </Col>
          <Col xs={24} md={14} lg={16}>
            <Card
              style={{
                boxShadow: "none",
                padding: "0px",
              }}
              bordered={false}
              bodyStyle={{ paddingTop: "0px" }}
              className={styles[`${componentName}__template-detail`]}
            >
              <div style={{ marginBottom: "1.5rem" }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    textAlign: "left",
                  }}
                >
                  Template name
                </Text>
                <Input value={template.name} readOnly style={{ width: "100%", fontWeight: "bold" }} />
              </div>

              <div>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    textAlign: "left",
                  }}
                >
                  Template
                </Text>
                <Input.TextArea
                  value={template.content}
                  readOnly
                  autoSize={{ minRows: 10, maxRows: 20 }}
                  style={{ width: "100%" }}
                />
              </div>

              <div style={{ marginTop: "2rem", textAlign: "right" }}>
                <Button type="primary" onClick={handleSave}>
                  Add & Edit Template
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default TemplateDetailsPage;
